/**
 * Service Settings Cache
 * 
 * Handles caching of downloaded service settings in session storage.
 * Cache keys include environment, productId, startDate, endDate, and effective timezone
 * to ensure proper cache invalidation when any of these change.
 */

import type { UATServiceSettingsResponse } from '../../../services/fixedRouteUatClient';

const CACHE_PREFIX = 'services-cache-';

/**
 * Generate cache key for service settings
 * 
 * @param environment - Environment (UAT/Production)
 * @param productId - Product ID
 * @param startDate - Start date (YYYY-MM-DD)
 * @param endDate - End date (YYYY-MM-DD)
 * @param timezone - Effective timezone
 * @returns Cache key string
 */
function getCacheKey(
  environment: string,
  productId: string,
  startDate: string,
  endDate: string,
  timezone: string
): string {
  return `${CACHE_PREFIX}${environment}-${productId}-${startDate}-${endDate}-${timezone}`;
}

/**
 * Get cached service settings response
 * 
 * @param environment - Environment (UAT/Production)
 * @param productId - Product ID
 * @param startDate - Start date (YYYY-MM-DD)
 * @param endDate - End date (YYYY-MM-DD)
 * @param timezone - Effective timezone
 * @returns Cached response or null if not found
 */
export function getCachedServicesList(
  environment: string,
  productId: string,
  startDate: string,
  endDate: string,
  timezone: string
): UATServiceSettingsResponse | null {
  try {
    const cacheKey = getCacheKey(environment, productId, startDate, endDate, timezone);
    const cached = sessionStorage.getItem(cacheKey);
    
    if (cached) {
      const parsed = JSON.parse(cached);
      console.log('[getCachedServicesList] Cache hit for key:', cacheKey);
      return parsed as UATServiceSettingsResponse;
    }
    
    console.log('[getCachedServicesList] Cache miss for key:', cacheKey);
    return null;
  } catch (error) {
    console.warn('[getCachedServicesList] Error reading cache:', error);
    return null;
  }
}

/**
 * Set cached service settings response
 * 
 * @param environment - Environment (UAT/Production)
 * @param productId - Product ID
 * @param startDate - Start date (YYYY-MM-DD)
 * @param endDate - End date (YYYY-MM-DD)
 * @param timezone - Effective timezone
 * @param response - Service settings response to cache
 */
export function setCachedServicesList(
  environment: string,
  productId: string,
  startDate: string,
  endDate: string,
  timezone: string,
  response: UATServiceSettingsResponse
): void {
  try {
    const cacheKey = getCacheKey(environment, productId, startDate, endDate, timezone);
    sessionStorage.setItem(cacheKey, JSON.stringify(response));
    console.log('[setCachedServicesList] Cached response for key:', cacheKey);
  } catch (error) {
    console.warn('[setCachedServicesList] Error writing cache:', error);
    // Session storage might be full - try to clear old entries
    try {
      // Clear entries older than 1 hour (rough estimate)
      const oneHourAgo = Date.now() - 60 * 60 * 1000;
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          // For now, just log - could implement timestamp-based cleanup later
          console.log('[setCachedServicesList] Found cache entry:', key);
        }
      }
    } catch (cleanupError) {
      console.warn('[setCachedServicesList] Error during cache cleanup:', cleanupError);
    }
  }
}

/**
 * Clear cached service settings for a specific key
 * 
 * @param environment - Environment (UAT/Production)
 * @param productId - Product ID
 * @param startDate - Start date (YYYY-MM-DD)
 * @param endDate - End date (YYYY-MM-DD)
 * @param timezone - Effective timezone
 */
export function clearCachedServicesList(
  environment: string,
  productId: string,
  startDate: string,
  endDate: string,
  timezone: string
): void {
  try {
    const cacheKey = getCacheKey(environment, productId, startDate, endDate, timezone);
    sessionStorage.removeItem(cacheKey);
    console.log('[clearCachedServicesList] Cleared cache for key:', cacheKey);
  } catch (error) {
    console.warn('[clearCachedServicesList] Error clearing cache:', error);
  }
}

/**
 * Clear all cached service settings
 */
export function clearAllCachedServices(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    console.log('[clearAllCachedServices] Cleared', keysToRemove.length, 'cache entries');
  } catch (error) {
    console.warn('[clearAllCachedServices] Error clearing all cache:', error);
  }
}

